import React, { useState } from 'react';
import {
    Typography,
    Grid,
    TextField,
    Button,
    Alert,
    Snackbar,
    CircularProgress,
    Box,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import { ContactContainer, ContactForm, ContactInfo } from './Contact.styles';
import { ContactChip, ContactChipsContainer } from '../About/About.styles';

import axios from 'axios';

const backendUrl =
    import.meta.env.MODE === 'development'
        ? `http://${import.meta.env.VITE_BACKEND_URL}`
        : `https://${import.meta.env.VITE_BACKEND_URL_PROD}`;

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: '',
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });

        // Clear error when user types
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: '',
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (
            !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
        ) {
            newErrors.email = 'Invalid email address';
        }

        if (!formData.phone.trim()) {
            newErrors.phone = 'Phone number is required';
        }

        if (!formData.message.trim()) {
            newErrors.message = 'Message is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const response = await axios.post(
                `${backendUrl}/contact`,
                formData
            );

            if (response.status === 200) {
                setSnackbar({
                    open: true,
                    message:
                        'Your message has been sent successfully! We will get back to you soon.',
                    severity: 'success',
                });
                // Reset form
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    message: '',
                });
            }
        } catch (error) {
            console.error('Error sending message:', error);
            setSnackbar({
                open: true,
                message: 'Failed to send message. Please try again later.',
                severity: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({
            ...snackbar,
            open: false,
        });
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                height: 'auto',
                py: { xs: 8, md: 12 }, // Add padding top/bottom (smaller on mobile, larger on desktop)
            }}
        >
            <ContactContainer id="contact">
                <Typography
                    variant="h2"
                    component="h2"
                    align="center"
                    color=" "
                    gutterBottom
                    sx={{ fontWeight: 600, mb: 6 }}
                >
                    Contact Us
                </Typography>

                <Typography
                    variant="h5"
                    align="center"
                    color="text.secondary"
                    sx={{ mb: 8, maxWidth: '800px', mx: 'auto' }}
                >
                    Ready to get started? Contact us today for a free quote or
                    to discuss your project needs.
                </Typography>

                <Grid container spacing={6}>
                    <Grid item xs={12} md={6}>
                        <ContactInfo>
                            <Typography
                                variant="h4"
                                gutterBottom
                                color="text.primary"
                            >
                                Get In Touch
                            </Typography>
                            <Typography
                                variant="body1"
                                paragraph
                                color="text.secondary"
                            >
                                We're here to help with all your handyman
                                service needs. Fill out the form and we'll get
                                back to you as soon as possible.
                            </Typography>
                            <Typography
                                variant="body1"
                                paragraph
                                color="text.secondary"
                            >
                                Alternatively, you can reach us directly:
                            </Typography>
                            <ContactChipsContainer>
                                <ContactChip
                                    icon={<PhoneIcon />}
                                    label="(503) 851-2701"
                                    color="primary"
                                    variant="contained"
                                    clickable
                                    component="a"
                                    href="tel:5038512701"
                                />
                                <ContactChip
                                    icon={<EmailIcon />}
                                    label="mark@dnrighthomeservices.com"
                                    color="primary"
                                    variant="contained"
                                    clickable
                                    component="a"
                                    href="mailto:mark@dnrighthomeservices.com"
                                />
                            </ContactChipsContainer>
                        </ContactInfo>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <ContactForm
                            component="form"
                            onSubmit={handleSubmit}
                            noValidate
                        >
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                error={!!errors.name}
                                helperText={errors.name}
                                required
                            />
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                error={!!errors.email}
                                helperText={errors.email}
                                required
                            />
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                error={!!errors.phone}
                                helperText={errors.phone}
                                required
                            />
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Message"
                                name="message"
                                multiline
                                rows={4}
                                value={formData.message}
                                onChange={handleChange}
                                error={!!errors.message}
                                helperText={errors.message}
                                required
                            />
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                size="large"
                                endIcon={
                                    loading ? (
                                        <CircularProgress
                                            size={20}
                                            color="inherit"
                                        />
                                    ) : (
                                        <SendIcon />
                                    )
                                }
                                disabled={loading}
                                sx={{ mt: 3, py: 1.5 }}
                                fullWidth
                            >
                                {loading ? 'Sending...' : 'Send Message'}
                            </Button>
                        </ContactForm>
                    </Grid>
                </Grid>

                <Box
                    sx={{
                        mt: 3,
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 500,
                            textAlign: 'center',
                            color: 'text.secondary',
                            fontSize: '1.1rem',
                        }}
                    >
                        CCB #253480 - licensed, bonded and insured
                    </Typography>
                </Box>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={snackbar.severity}
                        variant="filled"
                        sx={{ width: '100%' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </ContactContainer>
        </Box>
    );
};

export default Contact;
